/* eslint-disable no-underscore-dangle */
import { getQuery } from 'sutils';
import { IServerVars, IUserInfoServer, LiveCore, LiveRoleType, LiveSocketShutDown, LiveState } from '@tencent/live-components';
import { IDisconnectReason } from '@tencent/docs-connection';
import { MEETING_SCENE_SOURCE } from '@src/page/toc/common/meeting-room-manager/message_const';
import { MeetingRoomHelper } from '@src/page/toc/common/meeting-room-manager/roomEventHandler';
import { getUserMarkName } from '@src/page/toc/common/meeting-room-manager/server/api';
import { IRemoteControlManager } from '@src/slide/application/service/remote-control-manager/core/interface';
import { MOBILE_JOIN, StartPCPlay } from '@src/slide/application/service/remote-control-manager/core/constant';
import { shouldDisableNetwork } from '@src/slide/infrastructure/module/dependent/socket';

export default class RemoteControlManager extends LiveCore implements IRemoteControlManager {
    protected moduleName = MEETING_SCENE_SOURCE.RemoteControl;
    private playerStateMgr: MeetingRoomHelper;
    // ws 连接是否打开，基类提供的类似字段状态更新有问题，所以自己实现一个
    private isSocketOpen = false;
    // 触发自动演示时 userActivation.isActive 状态，用于判断自动全屏是否成功
    private isUserActivationActiveOnAutoPlay = true;


    public constructor() {
        super();
        this.resetLiveInfo();
    }

    public get docID(): string {
        return this.getDocId() || window.location.pathname.split('/').pop() || '';
    }

    public getDocId(): string {
        const reg = new RegExp('/meetingroom/player/slide/([^/]+?)/?$');
        return reg.exec(location.pathname)?.[1] ?? getQuery('docId');
    }

    /**
     * 如果存在遥控器房间的话，停止并关闭
     */
    public closeAndStopLive(): Promise<void> {
        return new Promise((resolve) => {
            if (this.roomInfo().roomid) {
                const onShutDown = () => {
                    resolve();
                    this.event.off(LiveSocketShutDown, onShutDown);
                };
                this.event.on(LiveSocketShutDown, onShutDown);
                this.liveEnd();
                this.closeShowRoom();
                // 兜底逻辑，避免由于其他原因 LiveSocketShutDown 事件没触发导致当前 promise 状态无法变更
                setTimeout(() => {
                    resolve();
                    this.event.off(LiveSocketShutDown, onShutDown);
                }, 500);
                return;
            }
            resolve();
        });
    }

    public liveBegin(eventArgs: any): boolean {
        this.sendLiveMessage('REVISION');
        super.liveBegin(eventArgs);
        return true;
    }

    public handleSocketOpen(): void {
        super.handleSocketOpen();
        this.isSocketOpen = true;
    }

    public handleSocketReopen() {
        super.handleSocketReopen();
        this.isSocketOpen = true;
    }

    public handleSocketShutDown(reason?: IDisconnectReason | undefined): void {
        super.handleSocketShutDown(reason);
        this.isSocketOpen = false;
        // 房间关闭后清除房间信息
        this._roomInfo = null;
    }


    // 从 LiveManagerV2 中复制，后续看是否需要抽象基类
    public get userId(): string {
        // - userId 带前缀 p./g.
        // - uid/toid/tinyid 不带前缀。
        //   历史存在长短id之分，uid两种id都可能有（绝大部分接口已经统一为长id），后两者都是长id。

        // 演示房间中需要带前缀的 userId 区分游客/登录用户


        // window.basicClientVars?.userInfo?.userId    ssr直出
        // window.clientVars?.userId                   data/get 接口返回
        // window.clientVars.userInfo                  userinfo接口返回；只有登录用户信息，且没有 userId，在此处无法使用
        // window.playerInitData?.userId               远程演示品类player准备就绪后写入
        return (
            window.clientVars?.userId
            || window.basicClientVars?.userInfo?.userId
            || window.playerInitData?.userId
        ) ?? '';
    }

    // 从 LiveManagerV2 中复制，后续看是否需要抽象基类
    public get serverVars(): IServerVars {
        // return this.playerInitData;
        return {
            padId: undefined,
            globalPadId: undefined,
            pver: undefined,
            gray: undefined,
            appid: undefined,
            wmode: undefined,
            client: {
                upid: undefined,
                sid: undefined,
                tid: undefined,
                sig: undefined,
            },
        };
    }
    // 从 LiveManagerV2 中复制，后续看是否需要抽象基类
    public async getUserInfos(uids: string[]): Promise<IUserInfoServer[] | false> {
        const padId = window.playerInitData?.globalPadId ?? window.clientVars?.globalPadId;
        const userInfos = await getUserMarkName(uids, padId);
        return userInfos ?? false;
    }
    // 从 LiveManagerV2 中复制，后续看是否需要抽象基类
    public setMeetingRoomHelper(playerStateMgr: MeetingRoomHelper) {
        this.playerStateMgr = playerStateMgr;
    }

    public get currentPage() {
        const { page } = this.liveInfo;
        return page > 1 ? page - 1 : 0;
    }
    /**
     * 发送翻页数据
     */
    public gotoPage() {
        const { playerState } = this.playerStateMgr;
        if (playerState && this._role === LiveRoleType.Master) {
            console.log(`live GotoPage: ${playerState.currentPage}`);
            const page = playerState.currentPage || 0;
            const fragment = playerState.currentFragment || 0;
            const iaFragmentInfo = playerState.iaFragmentInfo || {};
            // 发送时 page和fragment从1开始计数。
            this._liveInfo.page = page + 1;
            this._liveInfo.fragment = fragment + 1;
            (this._liveInfo as any).iaFragmentInfo = iaFragmentInfo;
            (this._liveInfo as any).shouldStopMainAnim = playerState.shouldStopMainAnim;
            (this._liveInfo as any).isLoadPageBySwitchForward = playerState.isLoadPageBySwitchForward;
            this.sendLiveMessage('PAGE');
        } else {
            console.log('live GotoPage failed');
        }
    }

    /**
     * 触发 pc 端进入演示态
     */
    public startPCPlay() {
        /**
         * 浏览器的安全策略：判断用户在"短暂激活"状态时自动全屏才能成功，这个状态就是 navigator.userActivation.isActive
         * 项目当前 ts 版本不支持 userActivation 的类型，所以转为 any
         */
        this.isUserActivationActiveOnAutoPlay = (navigator as any)?.userActivation?.isActive ?? true;
        this.event.emit(StartPCPlay, null);
        this.liveBegin({});
    }

    /**
     * 是否成功全屏，根据触发演示时浏览器的激活状态判断
     * @returns boolean
     */
    public get isAutoFullScreenSuccess(): boolean {
        return this.isUserActivationActiveOnAutoPlay;
    }

    public get remoteControlUrl(): string {
        const roomid = this.roomInfo().roomid || 0;
        if (!roomid) {
            return '';
        }
        const { hostname } = window.location;
        return `https://${hostname}/meetingroom/player/slide/${this.docID}?roomId=${roomid}&sceneSource=${MEETING_SCENE_SOURCE.RemoteControl}`;
    }

    /**
     * 更新自己创建的遥控器房间信息并进入
     * 如果没获取到房间会 reject
     * @returns
     */
    public initRoomInfoAndEnter = async () => {
        if (shouldDisableNetwork()) {
            return;
        }
        const { roomid } = this.roomInfo();
        if (this.isSocketOpen && roomid) {
            // ws 连接已经创建，无需重复初始化
            return;
        }
        if (!roomid) {
            await this.initRoomInfo();
        }
        // 进入房间
        this.masterEnter();
        this.setLiveState(LiveState.Pause);
    };

    /**
     * 获取自己创建的遥控器房间信息并更新 roomInfo
     * 如果没获取到房间会 reject
     * @returns
     */
    public initRoomInfo = async () => {
        try {
            const roomInfo = await this.getRemoteControlRoomInfo();
            this._roomInfo = Object.assign({}, this._roomInfo, roomInfo);
            return this._roomInfo;
        } catch (error) {
            this._roomInfo = null;
            throw error;
        }
    };

    public masterEnter() {
        // 避免重复创建 ws 连接
        if (this.isSocketOpen) {
            return;
        }
        super.masterEnter();
    }

    /**
     * 获取自己创建的遥控器房间信息
     */
    public async getRemoteControlRoomInfo() {
        const result = await this.getShowInfo();
        const extraInfo = await this.getRoomExtraInfo(result.roomid);
        if (extraInfo.moduleName === MEETING_SCENE_SOURCE.RemoteControl) {
            return result;
        }
        throw new Error('current room is not a remote control room');
    }

    public handleLiveMessage(data) {
        super.handleLiveMessage(data);
        // 接收到服务端发送的手机端加入的消息，触发自定义事件
        if (data.type === MOBILE_JOIN) {
            this.event.emit(MOBILE_JOIN);
        }
    }

    /**
     * 手机端调用，在加入房间后发送消息
     */
    public mobileJoin() {
        this.sendLiveMessage(MOBILE_JOIN);
    }

    // 遥控器默认都接收消息
    protected needReceiveLiveMessage = () => true;

    /**
     * 获取自己创建的房间信息
     * @returns
     */
    private getShowInfo() {
        return this.getShowRequest(this.docID).then((response) => {
            if (response?.data?.retcode === 0) {
                return response.data.result;
            }
            throw response.data;
        });
    }

    private async getRoomExtraInfo(roomid: string) {
        try {
            const roomInfo = await this.getRoomInfo(roomid);
            return JSON.parse(roomInfo!.info as string);
        } catch (err) {
            return {};
        }
    }
}
